import {
  FULL_MOON_TWISTS,
  fullMoonTwistFor,
  isFullMoonNight,
} from '@ringmaster/shared';
import { Glyph } from './Glyph.js';

/**
 * FullMoonBanner — "Full Moon Friday" callout for the lobbies, derived
 * ENTIRELY client-side from the shared calendar math (no server call: server
 * and client run the same UTC date functions, so no protocol traffic needed).
 *
 * NOTE the deliberate divergence: in dev the server can be forced into
 * full-moon mode via the FULL_MOON_FORCE env, which this banner will NOT
 * reflect — the client shows the calendar truth only. Acceptable: the force
 * flag is a dev/test lever, never set in prod.
 *
 * Two variants of one component (never fork a near-copy):
 *  - 'desktop' — the slim gold-on-dark banner in the Activity lobby, with the
 *    week's One Night twist line when the room is on that game.
 *  - 'phone'   — a single small line under the room-code area.
 *
 * No pulsing anywhere — the banner is static, so reduced motion needs no
 * special case.
 */
export function FullMoonBanner({
  gameId,
  variant,
}: {
  gameId: string;
  variant: 'desktop' | 'phone';
}) {
  const now = new Date();
  if (!isFullMoonNight(now)) return null;
  const twistId = fullMoonTwistFor(now);
  // The twist only reshapes the One Night pool — naming it on any other game
  // would promise something that table will never see.
  const twist = gameId === 'onenight' && twistId ? FULL_MOON_TWISTS[twistId] : null;

  if (variant === 'phone') {
    return (
      <p
        style={{
          margin: '8px 0 0',
          fontFamily: 'var(--font-ui)',
          fontSize: 12,
          letterSpacing: '0.06em',
          color: 'var(--gold-5, #e6b866)',
        }}
      >
        Full Moon Friday — double XP tonight.
      </p>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        width: '100%',
        maxWidth: 560,
        margin: '0 auto',
        padding: '8px 16px',
        borderRadius: 'var(--radius-pill)',
        border: '1px solid var(--gold-2, #7a4c1e)',
        background: 'rgba(230, 184, 102, 0.08)',
        flexWrap: 'wrap',
        textAlign: 'center',
      }}
    >
      <Glyph name="moonFull" size={18} tone="gold" />
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 16,
          letterSpacing: '0.05em',
          color: 'var(--gold-5, #e6b866)',
          whiteSpace: 'nowrap',
        }}
      >
        Full Moon Friday
      </span>
      <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
        Double XP tonight.
      </span>
      {twist && (
        <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-tertiary)' }}>
          <span style={{ color: 'var(--gold-5, #e6b866)' }}>{twist.name}</span> — {twist.line}
        </span>
      )}
    </div>
  );
}
